import { ButtonVariant } from "@repo/ui/Buttons/button";
import { iconButton } from "@repo/ui/Buttons/iconButton";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { type FC, useContext } from "react";
import { ToggleButton, ToggleGroupStateContext } from "react-aria-components";
import { tv } from "tailwind-variants";
import type { AssessmentFeedbackSentiment } from "./helpers.js";

export interface AssessmentFeedbackSentimentToggleButtonProps {
  sentiment: AssessmentFeedbackSentiment;
}

const sentimentComponents = {
  positive: {
    Icon: IconThumbUp,
  },
  negative: {
    Icon: IconThumbDown,
  },
} as const;

const sentimentIcon = tv({
  base: "size-4",
  variants: {
    isGroupSelectionEmpty: { true: "" },
    sentiment: { positive: "", negative: "" },
    isSelected: { true: "" },
  },
  compoundVariants: [
    {
      isGroupSelectionEmpty: false,
      isSelected: false,
      className: "opacity-30",
    },
    {
      isSelected: true,
      sentiment: "positive",
      className: "text-green-500",
    },
    {
      isSelected: true,
      sentiment: "negative",
      className: "text-red-500",
    },
  ],
});

/** Thumbs up/down toggle button. */
export const AssessmentFeedbackSentimentToggleButton: FC<
  AssessmentFeedbackSentimentToggleButtonProps
> = (props) => {
  const groupState = useContext(ToggleGroupStateContext);
  const isGroupSelectionEmpty = groupState?.selectedKeys.size === 0;

  const Icon = sentimentComponents[props.sentiment].Icon;

  return (
    <ToggleButton
      id={props.sentiment}
      className={iconButton({
        variant: ButtonVariant.Tertiary,
        className: "overflow-hidden pressed:bg-slate-200",
      })}
      aria-label={
        props.sentiment === "positive"
          ? "Share positive feedback about the AI assessment"
          : "Share negative feedback about the AI assessment"
      }
    >
      {({ isSelected }) => (
        <Icon
          className={sentimentIcon({
            isGroupSelectionEmpty,
            sentiment: props.sentiment,
            isSelected,
          })}
        />
      )}
    </ToggleButton>
  );
};
